import type {
  AppliedRuleAction,
  RuleViolation,
} from '@billing/validation/rules';
import {
  getApiErrorPayload,
  getApiResponseData,
  normalizeErrorMessage,
} from 'utils/errorUtils';

const unique = (values: string[]): string[] => Array.from(new Set(values));

const includesLabel = (message: string, label: string): boolean =>
  message.toLowerCase().includes(label.toLowerCase());

const formatRuleMessage = (
  message: string,
  label: string | null | undefined
): string => {
  if (!label) return message;
  return includesLabel(message, label) ? message : `${label}: ${message}`;
};

const extractRuleViolations = (payload: unknown): RuleViolation[] => {
  if (!payload || typeof payload !== 'object') return [];

  const rules = (payload as { rules?: unknown }).rules;
  if (!rules || typeof rules !== 'object') return [];

  const violations = (rules as { violations?: unknown }).violations;
  return Array.isArray(violations) ? (violations as RuleViolation[]) : [];
};

export const extractRuleViolationsFromError = (
  error: unknown
): RuleViolation[] => {
  const errorPayload = getApiErrorPayload(error);
  return errorPayload ? extractRuleViolations(errorPayload) : [];
};

export const formatRuleViolationMessages = (
  violations: RuleViolation[],
  severity?: RuleViolation['severity']
): string[] =>
  unique(
    violations
      .filter((violation) => !severity || violation.severity === severity)
      .map((violation) => {
        const message =
          normalizeErrorMessage(violation.message) ||
          normalizeErrorMessage(violation.code);
        if (!message) return null;
        const label =
          normalizeErrorMessage(violation.ruleSetName) ||
          normalizeErrorMessage(violation.ruleName);
        return formatRuleMessage(message, label);
      })
      .filter((value): value is string => Boolean(value))
  );

const extractAppliedRuleActions = (payload: unknown): AppliedRuleAction[] => {
  if (!payload || typeof payload !== 'object') return [];

  const rules = (payload as { rules?: unknown }).rules;
  if (!rules || typeof rules !== 'object') return [];

  const actions = (rules as { appliedActions?: unknown }).appliedActions;
  return Array.isArray(actions) ? (actions as AppliedRuleAction[]) : [];
};

export const extractRuleFailureMessages = (error: unknown): string[] => {
  const responseData = getApiResponseData(error);
  const errorPayload = getApiErrorPayload(error);
  if (!errorPayload) return [];

  const blockingViolationMessages = extractRuleViolations(errorPayload)
    .filter((violation) => violation?.severity === 'block')
    .map((violation) => {
      const message =
        normalizeErrorMessage(violation?.message) ||
        normalizeErrorMessage(violation?.code);
      if (!message) return null;

      const label =
        normalizeErrorMessage(violation?.ruleSetName) ||
        normalizeErrorMessage(violation?.ruleName);
      return formatRuleMessage(message, label);
    })
    .filter((value): value is string => Boolean(value));

  if (blockingViolationMessages.length) {
    return unique(blockingViolationMessages);
  }

  const fallback =
    normalizeErrorMessage(errorPayload.message) ||
    normalizeErrorMessage(responseData?.message);
  return fallback ? [fallback] : [];
};

export const extractRuleWarningMessages = (violations: unknown): string[] => {
  if (!Array.isArray(violations)) return [];

  const warningMessages = violations
    .map((violation) => {
      const typedViolation = violation as RuleViolation;
      if (typedViolation?.severity !== 'warning') return null;
      const message = normalizeErrorMessage(typedViolation?.message);
      if (!message) return null;

      const label =
        normalizeErrorMessage(typedViolation?.ruleSetName) ||
        normalizeErrorMessage(typedViolation?.ruleName);
      return formatRuleMessage(message, label);
    })
    .filter((value): value is string => Boolean(value));

  return unique(warningMessages);
};

export const extractRuleWarningMessagesFromSuccess = (
  payload: unknown
): string[] => {
  if (!payload || typeof payload !== 'object') return [];

  const violations = extractRuleViolations(payload);
  return extractRuleWarningMessages(violations);
};

export const extractAppliedRuleMessagesFromSuccess = (
  payload: unknown
): string[] => {
  const actions = extractAppliedRuleActions(payload);
  if (!actions.length) return [];

  return unique(
    actions
      .map((action) => {
        const field = normalizeErrorMessage(action.field) || 'unknown field';
        const label =
          normalizeErrorMessage(action.ruleSetName) ||
          normalizeErrorMessage(action.ruleName);
        const message = `Applied ${field}`;
        return label ? `${label}: ${message}` : message;
      })
      .filter(Boolean)
  );
};
